// @LTG-ID: data/journals/what-percentage-of-businesses-sell
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0107
// @CHANGED: 2026-07-06 (CHG-0107 · Journals content port from batch MD)
// @OWNER: web
//
// Article 7 of 20. Source: batch MD at /app/_ltg_analysis/content_batches/.
// Byline: LTG Team (per CHG-0107 orchestrator Decision 2 / batch preamble directive).

export const BRIEF = {
    SLUG: "what-percentage-of-businesses-sell",
    FRONTMATTER: {
        number: "44",
        section: "Exit Planning",
        sectionHref: "/journals#exit-planning",
        sectionAnchor: "exit-planning",
        title: "What Percentage of Businesses Actually Sell? The Statistics Every Owner Should See",
        deck: "Industry research consistently indicates that only a minority of small businesses listed for sale actually close a transaction, with figures commonly cited in the range of 20 to 30 percent.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-05-25",
        publishedHuman: "May 25, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 421,
        timeRequired: "PT3M",
        keywords: "what percentage of businesses sell, business sale statistics, businesses that never sell, exit planning statistics",
        articleSection: "Exit Planning",
        articleBodyPreview: "Industry research consistently indicates that only a minority of small businesses listed for sale actually close a transaction, with figures commonly cited in the range of 20 to 30 percent. The leading causes of failed sales are overpricing, owner dependence, poor financial records, and lack of preparation. Owners tend to assume that selling a business is like selling a house: list it, wait, close. The data says otherwise, and the gap between assumption and reality is where fortunes are lost. Th",
        complianceNote: "This article is educational. Statistics are attributed to their published sources and should be verified against each source's most recent edition.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "how-much-is-my-business-worth",
        "exit-planning-guide",
        "how-long-does-it-take-to-sell-a-business",
        "silver-tsunami-business-statistics"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "What Percentage of Businesses Actually Sell? The Statistics",
        canonical: "https://letstalkgrowth.com/journals/what-percentage-of-businesses-sell",
        ogImage: "/journals/og/what-percentage-of-businesses-sell.jpg",
    },
    BODY_MARKDOWN: `Industry research consistently indicates that only a minority of small businesses listed for sale actually close a transaction, with figures commonly cited in the range of 20 to 30 percent. The leading causes of failed sales are overpricing, owner dependence, poor financial records, and lack of preparation.

Owners tend to assume that selling a business is like selling a house: list it, wait, close. The data says otherwise, and the gap between assumption and reality is where fortunes are lost.

## The headline numbers

Several independent bodies of research point in the same direction:

- Organizations in the exit planning field, including the Exit Planning Institute, have long cited that only roughly 20 to 30 percent of businesses that go to market actually sell. The remainder are withdrawn, expire, or quietly close.
- The Exit Planning Institute's State of Owner Readiness research has repeatedly found that the large majority of owners have no formal transition plan, even though most of their personal net worth is tied up in the business.
- The same research tradition reports that a substantial share of owners who do sell express significant regret within a year of the transaction, driven more by personal unpreparedness than by price.
- BizBuySell Insight Reports, which track completed transactions quarterly, show that the businesses that do sell share recognizable traits: verifiable earnings, reasonable pricing relative to market comparables, and operations that transfer to a new owner.

Before citing any of these figures in your own planning or writing, check the current edition of each source; the exact percentages move year to year even as the pattern holds.

## Why most listed businesses fail to sell

The research and broker commentary converge on four causes.

**Overpricing.** Owners anchor on what they need rather than what the market pays. Buyers, who see many listings, simply pass.

**Owner dependence.** When the owner is the business, buyers correctly see that the asset they are buying may walk out the door at closing.

**Unverifiable financials.** Cash businesses, commingled personal expenses, and inconsistent records make diligence impossible. Buyers do not negotiate with uncertainty; they leave.

**No preparation.** Businesses brought to market in response to a trigger, such as health, burnout, or partner conflict, have had no time to fix any of the above.

## The optimistic reading

Every cause on that list is addressable, and none of them requires luck. The statistics are grim for unprepared sellers and encouraging for prepared ones, because preparation is scarce. In a market where most listings are flawed, a clean, transferable, sensibly priced business stands out immediately.`,
    FAQ: [
        { question: "What share of business owners have a written exit plan?", answer: "The Exit Planning Institute's State of Owner Readiness surveys have consistently found that most owners lack a written transition plan, a finding that has held across multiple survey years and regions." },
        { question: "Do most owners regret selling?", answer: "EPI-affiliated research has reported that a large share of former owners experience significant regret within a year of exiting, most often tied to loss of identity and purpose rather than the financial terms." },
        { question: "Where do these statistics come from?", answer: "Primary sources include the Exit Planning Institute's State of Owner Readiness reports, BizBuySell's quarterly Insight Reports, and the IBBA Market Pulse survey. Each publishes updated figures regularly, and current editions should be checked before quoting exact numbers." }
    ],
    SOURCES: [
        { id: "s1", html: "Exit Planning Institute, <em>State of Owner Readiness</em> research (multiple editions)." },
        { id: "s2", html: "BizBuySell, <em>Insight Reports</em> — quarterly closed-transaction data for U.S. small businesses." },
        { id: "s3", html: "International Business Brokers Association (IBBA), <em>Market Pulse</em> survey — quarterly." }
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal →",
        microLine: "· 7-DAY DELIVERY · KGOB METHODOLOGY, NC CPA #30420 ·",
    },
    READ_NEXT: [
        {
            tag: "Valuation",
            title: "How Much Is My Business Worth? The Owner's Complete Guide to Business Valuation",
            meta: "5 min · Brief 38",
            to: "/journals/how-much-is-my-business-worth",
        },
        {
            tag: "Exit Planning",
            title: "Exit Planning 101: How to Leave Your Business on Your Own Terms",
            meta: "4 min · Brief 39",
            to: "/journals/exit-planning-guide",
        },
        {
            tag: "Exit Planning",
            title: "How Long Does It Take to Sell a Business?",
            meta: "3 min · Brief 43",
            to: "/journals/how-long-does-it-take-to-sell-a-business",
        },
    ],
};

export default BRIEF;
